// biome-ignore-all lint/correctness/noRestrictedElements: Base Link Component, Anchor tag required
import { useRouter } from 'next/router';
import { type AnchorHTMLAttributes, type MouseEvent, useRef } from 'react';

import { isInternalLink, isSpecialLink } from '@/contentful';

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  // Default behavior is to prefetch on hover
  // This prop allows the option to turn off prefetch on hover for specific links
  noPrefetchOnHover?: boolean;
}

// Using anchor.current.href value for prefetching/navigation to allow ABTasty to override urls.
// Keeping global isInternalLink(href) check for disabled JS environments
export default function Link({
  href,
  onClick,
  onMouseEnter,
  children,
  noPrefetchOnHover = false,
  ...rest
}: LinkProps) {
  const router = useRouter();
  // Server side component level isInternal calculation for Javascript-disabled client environments
  const isInternal = isInternalLink(href);
  const isSpecial = isSpecialLink(href);
  const anchor = useRef<HTMLAnchorElement>(null);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    const target = anchor.current?.getAttribute('href') || href;
    const isTargetInternal = isInternalLink(target);

    // Maintain the default anchor functionality if the link is external or user tries to load link in new tab
    if (!isTargetInternal || e.metaKey || e.ctrlKey || e.shiftKey) {
      return true;
    }

    // Use client side navigation for internal links
    e.preventDefault();
    router.push(target || '');
  };

  // Prefetch internal links on hover
  const handleMouseEnter = (e: MouseEvent<HTMLAnchorElement>) => {
    const target = anchor.current?.getAttribute('href') || href;
    const isTargetInternal = isInternalLink(target);

    if (onMouseEnter) {
      onMouseEnter(e);
    }

    if (target && isTargetInternal && !noPrefetchOnHover) {
      router.prefetch(target);
    }
  };

  // Open link in a new tab if it is an external web link
  const externalWebLinkProps =
    !isInternal && !isSpecial ? { target: '_blank', rel: 'noreferrer' } : {};

  return (
    <a
      ref={anchor}
      href={href}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      {...externalWebLinkProps}
      {...rest}
    >
      {children}
    </a>
  );
}
